import React from 'react';
import {
  Box,
  Divider,
  FormControlLabel,
  InputAdornment,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { InfoOutlined as InfoIcon } from '@mui/icons-material';
import { formatTokensCompact } from '../../constants/aiUsage';

/**
 * The AI token allowance inputs.
 *
 * Extracted so Add Client, Edit Client and the JLL usage dialog present and
 * validate the allowance identically — the alternative was three drifting copies
 * of the same four fields.
 *
 * Controlled component: the parent owns `values` and validation `errors`.
 *
 * @param {object} props
 * @param {{inputTokenLimit: string, outputTokenLimit: string,
 *          alertThresholdPercent: string, isEnforced: boolean}} props.values
 * @param {object} [props.errors] field name -> message
 * @param {(field: string, value: any) => void} props.onChange
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.showHeading] hide when the parent already has a title
 */
const AiTokenLimitFields = ({
  values,
  errors = {},
  onChange,
  disabled = false,
  showHeading = true,
}) => {
  const handleNumberChange = (field) => (event) => {
    // Keep digits only: the API takes a whole token count, and stripping here
    // means the field can never show something the backend would reject.
    onChange(field, event.target.value.replace(/[^\d]/g, ''));
  };

  const tokenHelper = (raw, fallback) => {
    const value = Number(raw);
    if (!raw || Number.isNaN(value) || value <= 0) return fallback;
    return `${formatTokensCompact(value)} tokens`;
  };

  return (
    <Box>
      {showHeading && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              AI Token Limits
            </Typography>
            <Tooltip
              arrow
              title="Tokens inside the allowance are included. Once a limit is passed, further usage is charged at the rate of the model that ran."
            >
              <InfoIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
            </Tooltip>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Setting limits makes the AI Usage meter visible to this client&apos;s users.
            Leave both blank to skip for now.
          </Typography>
        </>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2,
        }}
      >
        <TextField
          id="inputTokenLimit"
          label="Input Token Limit"
          value={values.inputTokenLimit ?? ''}
          onChange={handleNumberChange('inputTokenLimit')}
          error={!!errors.inputTokenLimit}
          helperText={errors.inputTokenLimit || tokenHelper(values.inputTokenLimit, 'Tokens sent to the model')}
          disabled={disabled}
          fullWidth
          variant="outlined"
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
        />

        <TextField
          id="outputTokenLimit"
          label="Output Token Limit"
          value={values.outputTokenLimit ?? ''}
          onChange={handleNumberChange('outputTokenLimit')}
          error={!!errors.outputTokenLimit}
          helperText={errors.outputTokenLimit || tokenHelper(values.outputTokenLimit, 'Tokens generated by the model')}
          disabled={disabled}
          fullWidth
          variant="outlined"
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
        />

        <TextField
          id="alertThresholdPercent"
          label="Alert Threshold"
          value={values.alertThresholdPercent ?? ''}
          onChange={handleNumberChange('alertThresholdPercent')}
          error={!!errors.alertThresholdPercent}
          helperText={errors.alertThresholdPercent || 'Usage meter turns amber at this point'}
          disabled={disabled}
          fullWidth
          variant="outlined"
          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={!!values.isEnforced}
                onChange={(event) => onChange('isEnforced', event.target.checked)}
                disabled={disabled}
              />
            }
            label={
              <Box>
                <Typography variant="body2">Block AI at the limit</Typography>
                <Typography variant="caption" color="text.secondary">
                  {values.isEnforced
                    ? 'AI requests stop once a limit is reached'
                    : 'Overage is allowed and charged'}
                </Typography>
              </Box>
            }
          />
        </Box>
      </Box>
    </Box>
  );
};

export default AiTokenLimitFields;
